import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import { isEqual } from "lodash"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Row from "../../../common/components/Row"
import {
  PAGE_INDEX_SEARCH_PARAM,
  SORT_COLUMN_SEARCH_PARAM,
  SORT_DIRECTION_SEARCH_PARAM,
} from "../../../common/components/tables/utils"
import {
  readSavedViewDraft,
  removeSavedViewDraft,
  savedViewDraftKey,
  writeSavedViewDraft,
} from "../../../common/savedViewDrafts"
import {
  useNavigateUpdatedSearchParams,
  useSimpleSearchParamState,
} from "../../../common/utils"
import {
  sanitizeFilters,
  upgradeLegacyFilters,
} from "../../../smartsetter-utils/filters-dialog/filterSchema"
import { selectActiveAccountId, selectProfileLoaded } from "../../users/reducer"
import AddSavedFilterDialog from "./AddSavedFilterDialog"
import DeleteSavedFilterDialog from "./DeleteSavedFilterDialog"

export const SAVED_FILTER_PAGES = {
  candidates: "candidates",
  myMLS: "mymls",
  uploadedLeads: "uploadedleads",
}

const ALL_TAB_LABELS = {
  [SAVED_FILTER_PAGES.candidates]: "All candidates",
  [SAVED_FILTER_PAGES.myMLS]: "All agents",
  [SAVED_FILTER_PAGES.uploadedLeads]: "All leads",
}

const ALL_TAB_DRAFT_KEY = "all"

function resolveSavedFilterSort(
  savedFilter,
  defaultSortColumn,
  defaultSortDirection,
  activeSort = null
) {
  if (savedFilter?.sort_column) {
    return {
      sortColumn: savedFilter.sort_column,
      sortDirection: savedFilter.sort_direction || defaultSortDirection,
    }
  }
  return {
    sortColumn: activeSort?.sortColumn || defaultSortColumn,
    sortDirection: activeSort?.sortDirection || defaultSortDirection,
  }
}

function savedFilterSortPersisted(
  savedFilter,
  sortColumn,
  sortDirection,
  defaultSortColumn,
  defaultSortDirection
) {
  if (!savedFilter?.sort_column) {
    return (
      sortColumn === defaultSortColumn && sortDirection === defaultSortDirection
    )
  }
  return (
    sortColumn === savedFilter.sort_column &&
    sortDirection === (savedFilter.sort_direction || defaultSortDirection)
  )
}

function normalizeSavedFilters(data) {
  return Array.isArray(data) ? data : []
}

/**
 * Saved views (tabs above a list). A view saves on purpose, never by itself:
 * filter and sort edits apply to the list right away and are kept per view for
 * this browser tab until the user saves or resets them, so a concierge's
 * temporary dialing filter never rewrites a customer's view.
 */
export function useSavedFilterTabsState(
  page,
  debouncedFilters,
  setFilters,
  setPageIndex,
  {
    filters: filtersParam,
    normalizeFilters,
    sortColumn,
    sortDirection,
    setSort,
    defaultSortColumn,
    defaultSortDirection,
  }
) {
  const filters = filtersParam ?? debouncedFilters
  const TAB_INDEX_SEARCH_PARAM = "svdf"
  const [candidatesTabIndex, setCandidatesTabIndex] = useSimpleSearchParamState(
    TAB_INDEX_SEARCH_PARAM,
    0
  )
  const [savedFilters, setSavedFilters] = useState([])
  const [savedFiltersLoaded, setSavedFiltersLoaded] = useState(false)
  const [viewStateHydrated, setViewStateHydrated] = useState(false)
  const [addSavedFilterDialogOpen, setAddSavedFilterDialogOpen] =
    useState(false)
  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false)
  const [savedFilterToDelete, setSavedFilterToDelete] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()
  const axios = useAuthorizedAxiosClient()
  const profileLoaded = useSelector(selectProfileLoaded)
  const accountId = useSelector(selectActiveAccountId)

  const savedFiltersList = normalizeSavedFilters(savedFilters)
  const activeTabIndex = Math.min(
    Number(candidatesTabIndex) || 0,
    savedFiltersList.length
  )
  const activeView =
    activeTabIndex > 0 ? savedFiltersList[activeTabIndex - 1] || null : null

  // Old views are stored in the legacy shape until the migration runs; they
  // open (and compare) in the new one, so loading them is never a change.
  function comparable(filterList) {
    const upgraded = sanitizeFilters(upgradeLegacyFilters(filterList, page))
    return normalizeFilters ? normalizeFilters(upgraded) : upgraded
  }

  function upgradeView(view) {
    return view
      ? { ...view, filters: upgradeLegacyFilters(view.filters, page) }
      : view
  }

  function draftKeyFor(view) {
    return savedViewDraftKey(
      page,
      accountId,
      view ? view.id : ALL_TAB_DRAFT_KEY
    )
  }

  function viewForTab(tabIndex, list) {
    return tabIndex > 0 ? list[tabIndex - 1] || null : null
  }

  /** What a tab shows when opened: its unsaved edits, else the view as saved. */
  function tabState(view) {
    const draft = readSavedViewDraft(draftKeyFor(view))
    if (draft) {
      return {
        filters: upgradeLegacyFilters(draft.filters, page),
        sortColumn: draft.sortColumn || defaultSortColumn,
        sortDirection: draft.sortDirection || defaultSortDirection,
        fromDraft: true,
      }
    }
    if (!view) {
      return {
        filters: [],
        sortColumn: defaultSortColumn,
        sortDirection: defaultSortDirection,
        fromDraft: false,
      }
    }
    const sort = resolveSavedFilterSort(
      view,
      defaultSortColumn,
      defaultSortDirection,
      { sortColumn, sortDirection }
    )
    return { filters: view.filters || [], ...sort, fromDraft: false }
  }

  function applyTabState(state) {
    setFilters(state.filters)
    setSort(state.sortColumn, state.sortDirection, { updateUrl: false })
  }

  function handleCandidatesTabIndexChanged(e, tabIndex, savedFiltersParam) {
    const availableSavedFilters = normalizeSavedFilters(
      savedFiltersParam || savedFilters
    )
    const safeTabIndex = tabIndex > availableSavedFilters.length ? 0 : tabIndex
    const state = tabState(viewForTab(safeTabIndex, availableSavedFilters))

    setViewStateHydrated(false)
    setCandidatesTabIndex(safeTabIndex)
    setPageIndex(1, false)
    applyTabState(state)

    navigateUpdatedSearchParams({
      [TAB_INDEX_SEARCH_PARAM]: safeTabIndex,
      [PAGE_INDEX_SEARCH_PARAM]: 1,
      [SORT_COLUMN_SEARCH_PARAM]: state.sortColumn,
      [SORT_DIRECTION_SEARCH_PARAM]: state.sortDirection,
    })
  }

  function addViewAndOpen(savedFilter) {
    const newSavedFilters = [
      ...normalizeSavedFilters(savedFilters),
      upgradeView(savedFilter),
    ]
    setSavedFilters(newSavedFilters)
    handleCandidatesTabIndexChanged(
      null,
      newSavedFilters.length,
      newSavedFilters
    )
  }

  function handleAddSavedFilterDialogClosed(savedFilter) {
    if (savedFilter) {
      addViewAndOpen(savedFilter)
    }
    setAddSavedFilterDialogOpen(false)
  }

  function handleSaveAsDialogClosed(savedFilter) {
    if (savedFilter) {
      // The edits now live in the new view; the tab they came from opens as saved.
      removeSavedViewDraft(draftKeyFor(activeView))
      addViewAndOpen(savedFilter)
    }
    setSaveAsDialogOpen(false)
  }

  function handleDeleteSavedFilterButtonClicked(e, savedFilter) {
    e.stopPropagation()
    setSavedFilterToDelete(savedFilter)
  }

  function handleDeleteSavedFilterDialogClosed(deleted) {
    if (deleted) {
      removeSavedViewDraft(draftKeyFor(savedFilterToDelete))
      const remaining = normalizeSavedFilters(savedFilters).filter(
        (savedFilter) => savedFilter.id !== savedFilterToDelete.id
      )
      // move to previous tab if deleting current tab
      const indexInCandidatesTabs =
        normalizeSavedFilters(savedFilters).findIndex(
          (savedFilter) => savedFilter.id === savedFilterToDelete.id
        ) + 1
      if (activeTabIndex === indexInCandidatesTabs) {
        handleCandidatesTabIndexChanged(null, activeTabIndex - 1, remaining)
      } else if (activeTabIndex > indexInCandidatesTabs) {
        // The open view keeps its place in the URL's tab index.
        setCandidatesTabIndex(activeTabIndex - 1)
        navigateUpdatedSearchParams({
          [TAB_INDEX_SEARCH_PARAM]: activeTabIndex - 1,
        })
      }
      setSavedFilters(remaining)
    }
    setSavedFilterToDelete(null)
  }

  function replaceView(view) {
    setSavedFilters((current) =>
      normalizeSavedFilters(current).map((savedFilter) =>
        savedFilter.id === view.id ? view : savedFilter
      )
    )
  }

  function currentPayload() {
    return {
      filters: sanitizeFilters(filters),
      sort_column: sortColumn,
      sort_direction: sortDirection,
    }
  }

  function handleSaveView() {
    if (!activeView || isSaving) {
      return
    }
    setIsSaving(true)
    axios
      .put(`/crm/api/saved-filter/${activeView.id}/`, currentPayload())
      .then(({ data }) => {
        const saved = upgradeView(data)
        replaceView(saved)
        removeSavedViewDraft(draftKeyFor(saved))
        // Show what the server kept (it normalises filters on save).
        if (!isEqual(comparable(saved.filters), comparable(filters))) {
          setFilters(saved.filters)
        }
      })
      .finally(() => setIsSaving(false))
  }

  function handleResetView() {
    removeSavedViewDraft(draftKeyFor(activeView))
    const state = tabState(activeView)
    applyTabState(state)
    setPageIndex(1, false)
    navigateUpdatedSearchParams({
      [PAGE_INDEX_SEARCH_PARAM]: 1,
      [SORT_COLUMN_SEARCH_PARAM]: state.sortColumn,
      [SORT_DIRECTION_SEARCH_PARAM]: state.sortDirection,
    })
  }

  function handleDismissFiltersNotice() {
    if (!activeView) {
      return
    }
    replaceView({ ...activeView, filters_updated_notice_at: null })
    axios
      .patch(`/crm/api/saved-filter/${activeView.id}/`, {
        filters_updated_notice_at: null,
      })
      .catch(() => {
        // Shown again next load; nothing else depends on it.
      })
  }

  useEffect(
    () => {
      if (!profileLoaded) {
        return
      }
      let cancelled = false
      axios
        .get("/crm/api/saved-filter/", { params: { page } })
        .then(({ data }) => {
          if (cancelled) {
            return
          }
          const views = normalizeSavedFilters(data).map(upgradeView)
          setSavedFilters(views)
          const requestedTabIndex = Number(candidatesTabIndex) || 0
          if (requestedTabIndex > views.length) {
            handleCandidatesTabIndexChanged(null, 0, views)
            return
          }
          const state = tabState(viewForTab(requestedTabIndex, views))
          // The main list starts as the page left it (a link's filters, a
          // restored list); only remembered edits replace that.
          if (requestedTabIndex === 0 && !state.fromDraft) {
            return
          }
          setViewStateHydrated(false)
          applyTabState(state)
          navigateUpdatedSearchParams({
            [SORT_COLUMN_SEARCH_PARAM]: state.sortColumn,
            [SORT_DIRECTION_SEARCH_PARAM]: state.sortDirection,
          })
        })
        .catch(() => {
          if (!cancelled) {
            setSavedFilters([])
          }
        })
        .finally(() => {
          if (!cancelled) {
            setSavedFiltersLoaded(true)
          }
        })
      return () => {
        cancelled = true
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, axios, profileLoaded]
  )

  // A tab counts as hydrated once the filters it opened with have reached the
  // debounced list filters, so neither the list query nor the unsaved-changes
  // check runs on the previous tab's filters.
  useEffect(() => {
    if (!savedFiltersLoaded || viewStateHydrated) {
      return
    }
    if (isEqual(comparable(debouncedFilters), comparable(filters))) {
      setViewStateHydrated(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFiltersLoaded, viewStateHydrated, debouncedFilters, filters])

  const filtersChanged =
    !!activeView &&
    !isEqual(comparable(filters), comparable(activeView.filters))
  const sortChanged =
    !!activeView &&
    !savedFilterSortPersisted(
      activeView,
      sortColumn,
      sortDirection,
      defaultSortColumn,
      defaultSortDirection
    )
  const isViewDirty =
    savedFiltersLoaded && viewStateHydrated && (filtersChanged || sortChanged)
  const mainListHasState =
    activeTabIndex === 0 &&
    (sanitizeFilters(filters).length > 0 ||
      sortColumn !== defaultSortColumn ||
      sortDirection !== defaultSortDirection)

  // Remember the open tab's unsaved edits for this browser tab.
  useEffect(
    () => {
      if (!savedFiltersLoaded || !viewStateHydrated) {
        return
      }
      if (activeTabIndex > 0 && !activeView) {
        return
      }
      const key = draftKeyFor(activeView)
      const keep = activeView ? isViewDirty : mainListHasState
      if (keep) {
        writeSavedViewDraft(key, {
          filters: sanitizeFilters(filters),
          sortColumn,
          sortDirection,
        })
      } else {
        removeSavedViewDraft(key)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      savedFiltersLoaded,
      viewStateHydrated,
      activeTabIndex,
      activeView,
      isViewDirty,
      mainListHasState,
      filters,
      sortColumn,
      sortDirection,
    ]
  )

  const unsavedViewIds = new Set(
    savedFiltersList
      .filter((view) =>
        view.id === activeView?.id
          ? isViewDirty
          : !!readSavedViewDraft(draftKeyFor(view))
      )
      .map((view) => view.id)
  )

  return {
    page,
    candidatesTabIndex: activeTabIndex,
    savedFilters,
    savedFiltersReady: savedFiltersLoaded && viewStateHydrated,
    addSavedFilterDialogOpen,
    setAddSavedFilterDialogOpen,
    saveAsDialogOpen,
    setSaveAsDialogOpen,
    savedFilterToDelete,
    isViewDirty,
    unsavedViewIds,
    canSaveMainListAsView:
      savedFiltersLoaded &&
      activeTabIndex === 0 &&
      sanitizeFilters(filters).length > 0,
    isSaving,
    saveAsPayload: currentPayload(),
    filtersUpdatedNotice: !!activeView?.filters_updated_notice_at,
    handleCandidatesTabIndexChanged,
    handleAddSavedFilterDialogClosed,
    handleSaveAsDialogClosed,
    handleDeleteSavedFilterButtonClicked,
    handleDeleteSavedFilterDialogClosed,
    handleSaveView,
    handleResetView,
    handleDismissFiltersNotice,
  }
}

function UnsavedDot() {
  return (
    <Box
      component="span"
      aria-label="Unsaved changes"
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: "primary.main",
        display: "inline-block",
        ml: 0.75,
        verticalAlign: "middle",
      }}
    />
  )
}

export default function SavedFilterTabs({
  page,
  candidatesTabIndex,
  savedFilters,
  addSavedFilterDialogOpen,
  saveAsDialogOpen,
  savedFilterToDelete,
  setAddSavedFilterDialogOpen,
  setSaveAsDialogOpen,
  isViewDirty,
  unsavedViewIds,
  canSaveMainListAsView,
  isSaving,
  saveAsPayload,
  filtersUpdatedNotice,
  handleCandidatesTabIndexChanged,
  handleDeleteSavedFilterButtonClicked,
  handleAddSavedFilterDialogClosed,
  handleSaveAsDialogClosed,
  handleDeleteSavedFilterDialogClosed,
  handleSaveView,
  handleResetView,
  handleDismissFiltersNotice,
}) {
  const [saveMenuAnchor, setSaveMenuAnchor] = useState(null)
  const savedFiltersList = normalizeSavedFilters(savedFilters)
  const tabsValue = Math.min(
    Number(candidatesTabIndex) || 0,
    savedFiltersList.length
  )

  return (
    <>
      <Row sx={{ alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Tabs
          value={tabsValue}
          onChange={handleCandidatesTabIndexChanged}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minWidth: 0, flexShrink: 1 }}
        >
          <Tab label={ALL_TAB_LABELS[page] || "All"} />
          {savedFiltersList.map((savedFilter) => (
            <Tab
              key={savedFilter.id}
              label={
                <span>
                  {savedFilter.name}
                  {unsavedViewIds?.has(savedFilter.id) ? <UnsavedDot /> : null}
                </span>
              }
              icon={
                <IconButton
                  size="small"
                  onClick={(e) =>
                    handleDeleteSavedFilterButtonClicked(e, savedFilter)
                  }
                >
                  <CloseOutlinedIcon sx={{ fontSize: "15px" }} />
                </IconButton>
              }
              iconPosition="end"
            />
          ))}
        </Tabs>
        <Button
          variant="text"
          color="primary"
          startIcon={<AddOutlinedIcon variant="b600" />}
          sx={{ fontWeight: 500 }}
          onClick={() => setAddSavedFilterDialogOpen(true)}
        >
          Add view
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {canSaveMainListAsView ? (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => setSaveAsDialogOpen(true)}
          >
            Save as view
          </Button>
        ) : null}
        {isViewDirty ? (
          <Row sx={{ gap: 1, alignItems: "center" }}>
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={handleResetView}
              disabled={isSaving}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              endIcon={<ArrowDropDownIcon />}
              disabled={isSaving}
              onClick={(e) => setSaveMenuAnchor(e.currentTarget)}
            >
              Save
            </Button>
            <Menu
              open={Boolean(saveMenuAnchor)}
              anchorEl={saveMenuAnchor}
              onClose={() => setSaveMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setSaveMenuAnchor(null)
                  handleSaveView()
                }}
              >
                Save view
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSaveMenuAnchor(null)
                  setSaveAsDialogOpen(true)
                }}
              >
                Save as new view
              </MenuItem>
            </Menu>
          </Row>
        ) : null}
        <AddSavedFilterDialog
          open={addSavedFilterDialogOpen}
          onClose={handleAddSavedFilterDialogClosed}
          page={page}
        />
        <AddSavedFilterDialog
          open={!!saveAsDialogOpen}
          onClose={handleSaveAsDialogClosed}
          page={page}
          payload={saveAsPayload}
          title="Save as new view"
        />
        <DeleteSavedFilterDialog
          open={!!savedFilterToDelete}
          onClose={handleDeleteSavedFilterDialogClosed}
          savedFilter={savedFilterToDelete}
        />
      </Row>
      {filtersUpdatedNotice ? (
        <Alert
          severity="info"
          onClose={handleDismissFiltersNotice}
          sx={{ mb: 1 }}
        >
          We updated this view's filters.
        </Alert>
      ) : null}
    </>
  )
}
